/**
 * Creates a toggle delete handler for grid rows that:
 * 1. Reverts to original data when marking for deletion (discards edits)
 * 2. Reverts to original data when undoing deletion (discards all changes)
 * 3. Cancels any row in edit mode before updating (to ensure edit state is properly cleaned up)
 *
 * @param {Object} params
 * @param {Object} params.gridRef - Reference to the grid component
 * @param {string} params.dataKey - The primary key field name (e.g., 'sapCode', 'sapLineNum')
 * @param {Array|Object} params.originalData - Original data before any edits
 * @param {Function} params.transformOriginal - Function to transform original data for grid display
 * @param {Function} params.onDelete - Redux action to dispatch when marking for deletion
 * @param {Function} params.onUndoDelete - Redux action to dispatch when undoing deletion
 * @param {Function} params.onDeleteComplete - Optional callback (keyValue) => void called after delete completes
 * @returns {Function} Handler function (rowData) => void
 */
export const createToggleDeleteHandler = ({
    gridRef,
    dataKey,
    originalData,
    transformOriginal,
    onDelete,
    onUndoDelete,
    onDeleteComplete
}) => {
    return (rowData) => {
        // Cancel any row in edit mode before processing the delete toggle
        // This ensures the edit state is properly cleaned up (stopEditing is called)
        const cancelButton = document.querySelector('.p-row-editor-cancel')
        if (cancelButton) {
            cancelButton.click()
            // Wait for the cancel to complete before processing the delete toggle
            // This ensures the editingRowKey state and contributionEditRowCount are properly updated
            requestAnimationFrame(() => {
                processDeleteToggle()
            })
        } else {
            processDeleteToggle()
        }

        function processDeleteToggle() {
            const currentGridData = gridRef.current.getData()
            const keyValue = rowData[dataKey]

            const correctIndex = currentGridData.findIndex(
                (item) => item[dataKey] === keyValue
            )

            if (correctIndex === -1) {
                console.error("Row not found for deletion.")
                return
            }

            // Find the original data
            const originalItem = Array.isArray(originalData)
                ? originalData.find(item => item[dataKey] === keyValue)
                : originalData?.[keyValue]

            if (rowData.isDeleted) {
                // Undo delete - revert to original data completely (no changes)
                onUndoDelete(keyValue)

                if (originalItem) {
                    const transformedOriginal = transformOriginal(originalItem, false)
                    gridRef.current.updateRow(correctIndex, transformedOriginal)
                } else {
                    const updatedRowData = { ...currentGridData[correctIndex], isDeleted: false }
                    gridRef.current.updateRow(correctIndex, updatedRowData)
                }
            } else {
                // Mark for deletion - revert to original data and mark as deleted
                onDelete(keyValue)

                if (originalItem) {
                    const transformedOriginal = transformOriginal(originalItem, true)
                    gridRef.current.updateRow(correctIndex, transformedOriginal)
                } else {
                    const updatedRowData = { ...currentGridData[correctIndex], isDeleted: true }
                    gridRef.current.updateRow(correctIndex, updatedRowData)
                }
                // Call cleanup callback if provided
                if (onDeleteComplete) {
                    onDeleteComplete(keyValue)
                }
            }
        }
    }
}
